import { LightSigningSurface } from './LightSigningSurface';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
  Modal,
  ModalDialog,
  Stack,
  IconButton,
  ButtonGroup,
} from '@mui/joy';
import {
  User as Person,
  Buildings as Business,
  SignOut as ExitToApp,
  CheckCircle,
  Warning,
} from '@phosphor-icons/react';
import { pdfjs, ensureWorkerReady } from '../../../lib/pdfWorkerSetup';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { api } from '../../../lib/api';
import { authService } from '../../../lib/auth';
import SigningFieldRenderer from './SigningFieldRenderer';
import { SignatureModal } from './SignatureModal';
import type { ContractField as BaseContractField } from '../../../types';

// API Contract Field type that matches the actual API response
interface ApiContractField {
  id: number;
  field_type: string;
  type?: string;
  name?: string;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
  signer: string;
  label?: string;
  options?: string | string[];
  value?: any;
  placeholder?: string;
}

interface Contract {
  id: number;
  contract_number: string;
  customer_name: string;
  customer_email?: string;
  sales_rep_name?: string;
  manager_name?: string;
  pdf_url?: string;
  contract_fields: ApiContractField[];
  contract_data?: Record<string, any>;
  status?: string;
}

type SignerMode = 'customer' | 'sales_rep';

const SIGNER_CONFIG = {
  customer: {
    label: 'Customer',
    icon: Person,
    color: 'primary',
    signerTypes: ['customer'],
  },
  sales_rep: {
    label: 'Sales Rep',
    icon: Business,
    color: 'success',
    signerTypes: ['sales_rep', 'manager', 'witness'],
  },
};

const getSignerName = (contract: Contract, signerType: string): string => {
  if (SIGNER_CONFIG.customer.signerTypes.includes(signerType)) {
    return contract.customer_name || 'Customer';
  }
  if (signerType === 'manager') {
    return contract.manager_name || 'Manager';
  }
  if (signerType === 'witness') {
    return 'Witness';
  }
  return contract.sales_rep_name || 'Sales Rep';
};

const InPersonSigningInterfaceContent: React.FC = () => {
  const { contractId } = useParams<{ contractId: string }>();
  const navigate = useNavigate();

  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const [signerMode, setSignerMode] = useState<SignerMode>('customer');
  const [numPages, setNumPages] = useState<number | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [activeSignatureField, setActiveSignatureField] = useState<string | null>(null);
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  // Mirrors fieldValues synchronously so back-to-back autosaves send every value,
  // not just the one being edited
  const fieldValuesRef = useRef<Record<string, string>>({});
  const [containerWidth, setContainerWidth] = useState<number>(800);
  const [pageScale, setPageScale] = useState<number>(1);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());

  // Load contract data and fetch PDF with auth (using PDF.js directly)
  useEffect(() => {
    let cancelled = false;

    const loadContract = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/crm/contracts/${contractId}/`);
        const contractData = response.data as Contract;
        if (cancelled) return;

        setContract(contractData);

        // Initialize field values, preferring anything already stored in contract_data
        const initialValues: Record<string, string> = {};
        const savedValues = contractData.contract_data || {};
        contractData.contract_fields.forEach((field: ApiContractField) => {
          if (!field.type && field.field_type) {
            field.type = field.field_type;
          }
          const fieldId = String(field.id);
          const savedValue = savedValues[fieldId];
          if (savedValue !== undefined && savedValue !== null && savedValue !== '') {
            initialValues[fieldId] = String(savedValue);
          } else if (field.value) {
            initialValues[fieldId] = String(field.value);
          }
        });
        fieldValuesRef.current = initialValues;
        setFieldValues(initialValues);

        // Fetch PDF with auth headers
        const pdfUrl = contractData.pdf_url || `/api/crm/contracts/${contractId}/view_pdf/`;
        let pdfBytes: Uint8Array | null = null;
        try {
          const accessToken = authService.getAccessToken();
          const headers: Record<string, string> = {};
          if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`;
          }
          const pdfResponse = await fetch(pdfUrl, { headers });
          if (pdfResponse.ok) {
            pdfBytes = new Uint8Array(await pdfResponse.arrayBuffer());
          } else {
            const cookieResponse = await fetch(pdfUrl, { credentials: 'include' });
            if (cookieResponse.ok) {
              pdfBytes = new Uint8Array(await cookieResponse.arrayBuffer());
            }
          }
        } catch {
          // PDF fetch failed — continue without PDF
        }

        if (cancelled) return;

        // Load PDF with PDF.js directly (avoids react-pdf Strict Mode crashes)
        if (pdfBytes) {
          await ensureWorkerReady();
          const loadingTask = pdfjs.getDocument({ data: pdfBytes });
          const pdf = await loadingTask.promise;
          if (cancelled) {
            pdf.destroy();
            return;
          }
          setPdfDoc(pdf as any);
          setNumPages(pdf.numPages);
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load contract');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (contractId) {
      loadContract();
    }

    return () => {
      cancelled = true;
    };
  }, [contractId]);

  // Render all PDF pages to canvases when pdfDoc or containerWidth changes
  useEffect(() => {
    if (!pdfDoc || !numPages || containerWidth <= 0) return;
    let cancelled = false;

    const renderAllPages = async () => {
      for (let i = 1; i <= numPages; i++) {
        if (cancelled) return;
        const canvas = canvasRefs.current.get(i);
        if (!canvas) continue;

        try {
          const page = await pdfDoc.getPage(i);
          // Scale to fill container width
          const unscaledViewport = page.getViewport({ scale: 1 });
          const computedScale = containerWidth / unscaledViewport.width;
          const viewport = page.getViewport({ scale: computedScale });

          // Store the scale so SigningFieldRenderer can position fields correctly
          if (i === 1) setPageScale(computedScale);

          canvas.width = viewport.width;
          canvas.height = viewport.height;

          const ctx = canvas.getContext('2d');
          if (!ctx) continue;

          await page.render({ canvasContext: ctx, viewport, canvas } as any).promise;
        } catch (err) {
          if ((err as any)?.name !== 'RenderingCancelledException') {
            console.error(`Failed to render page ${i}:`, err);
          }
        }
      }
    };

    renderAllPages();

    return () => { cancelled = true; };
  }, [pdfDoc, numPages, containerWidth]);

  // Measure container width for responsive PDF rendering
  useEffect(() => {
    const el = pdfContainerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Handle field value changes
  const handleFieldChange = useCallback(async (fieldId: string, value: any) => {
    // Convert boolean to string for API
    const stringValue = typeof value === 'boolean' ? value.toString() : value;

    const nextValues = { ...fieldValuesRef.current, [fieldId]: stringValue };
    fieldValuesRef.current = nextValues;
    setFieldValues(nextValues);

    // Auto-save every captured value, not just the field that changed
    try {
      setSaving(true);

      const updatedFields = contract?.contract_fields.map((field) => {
        const savedValue = nextValues[String(field.id)];
        return savedValue !== undefined ? { ...field, value: savedValue } : field;
      }) || [];

      const updatedContractData = {
        ...(contract?.contract_data || {}),
        ...nextValues
      };

      await api.patch(`/api/crm/contracts/${contractId}/`, {
        contract_fields: updatedFields,
        contract_data: updatedContractData
      });
      setError(null);
    } catch {
      setError('Failed to save your last entry. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  }, [contract, contractId]);

  // Filter fields based on current signer mode
  const getVisibleFields = useCallback(() => {
    if (!contract) return [];

    const allowedSigners = SIGNER_CONFIG[signerMode].signerTypes;
    return contract.contract_fields.filter(field => allowedSigners.includes(field.signer));
  }, [contract, signerMode]);

  // Calculate completion progress (plain function — always uses latest state)
  const calculateProgress = (mode: SignerMode) => {
    if (!contract) return { completed: 0, total: 0, percentage: 0 };

    const allowedSigners = SIGNER_CONFIG[mode].signerTypes;
    const modeFields = contract.contract_fields.filter(field =>
      allowedSigners.includes(field.signer) && field.required
    );

    const completedFields = modeFields.filter(field => {
      const val = fieldValues[String(field.id)];
      return val != null && String(val).trim() !== '';
    });

    const percentage = modeFields.length > 0
      ? Math.round((completedFields.length / modeFields.length) * 100)
      : 100;

    return {
      completed: completedFields.length,
      total: modeFields.length,
      percentage,
    };
  };

  // Check if all required fields are completed
  const isSigningComplete = useCallback(() => {
    const customerProgress = calculateProgress('customer');
    const salesRepProgress = calculateProgress('sales_rep');

    return customerProgress.percentage === 100 && salesRepProgress.percentage === 100;
  }, [calculateProgress]);

  // Handle exit confirmation
  const handleExit = () => {
    if (!isSigningComplete()) {
      setShowExitDialog(true);
    } else {
      navigate(`/crm/contracts/${contractId}`);
    }
  };

  // Handle signature field click
  const handleSignatureFieldClick = useCallback((fieldId: string) => {
    setActiveSignatureField(fieldId);
    setSignatureModalOpen(true);
  }, []);

  // Handle signature save
  const handleSignatureSave = useCallback(async (signatureData: string) => {
    if (!activeSignatureField) return;

    // Save signature data like any other field
    await handleFieldChange(activeSignatureField, signatureData);
    setSignatureModalOpen(false);
    setActiveSignatureField(null);
  }, [activeSignatureField, handleFieldChange]);

  // Handle complete signing
  const handleCompleteSigning = async () => {
    if (!contract || !isSigningComplete()) {
      setError('Please complete all required fields before finishing');
      return;
    }

    try {
      setSaving(true);

      // Record the captured signatures and complete the contract in one call so
      // signature records, completion date and notifications all happen server-side
      const signedAt = new Date().toISOString();
      const signatures = contract.contract_fields
        .filter((field) => {
          const fieldType = field.type || field.field_type;
          const capturedValue = fieldValues[String(field.id)];
          return (fieldType === 'signature' || fieldType === 'initial') &&
            typeof capturedValue === 'string' &&
            capturedValue.startsWith('data:image');
        })
        .map((field) => {
          const isCustomerField = SIGNER_CONFIG.customer.signerTypes.includes(field.signer);
          return {
            signature_data_url: fieldValues[String(field.id)],
            signer_name: getSignerName(contract, field.signer),
            signer_email: isCustomerField ? contract.customer_email || '' : '',
            signer_title: field.label || field.signer,
            timestamp: signedAt,
          };
        });

      await api.post('/api/crm/contracts/complete_offline_signing/', {
        contract_id: contract.id,
        offline_id: `in_person_${contract.id}_${Date.now()}`,
        form_data: { ...(contract.contract_data || {}), ...fieldValues },
        signatures,
        signed_at: signedAt,
        customer_name: contract.customer_name,
        sales_rep: contract.sales_rep_name || '',
      });

      // Try to generate flattened PDF
      try {
        // Call regenerate_signed_pdf action if available
        await api.post(`/api/crm/contracts/${contractId}/regenerate_signed_pdf/`, {
          field_values: fieldValues
        });
      } catch (pdfError) {
        // Not critical if PDF generation fails, contract is still marked as completed
      }

      setError(null);
      setCompleted(true);

      // Navigate back to contract detail
      setTimeout(() => {
        navigate(`/crm/contracts/${contractId}`);
      }, 1500);

    } catch (err) {
      const detail = (err as any)?.responseData?.error;
      setError(detail || 'Failed to complete signing. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Use the actual scale computed from PDF page dimensions during rendering
  const scale = pageScale;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!contract) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger">{error || 'Contract not found'}</Alert>
      </Box>
    );
  }

  const visibleFields = getVisibleFields();



  // Convert API fields to the format expected by SigningFieldRenderer
  const convertedFields: BaseContractField[] = visibleFields.map(field => ({
    id: String(field.id),
    type: (field.type || field.field_type || 'text') as any,
    page: field.page,
    x: field.x,
    y: field.y,
    width: field.width,
    height: field.height,
    required: field.required,
    signer: field.signer,
    label: field.label,
    options: typeof field.options === 'string'
      ? field.options.split(',').map((o: string) => o.trim())
      : field.options || [],
    value: field.value
  }));


  const customerProgress = calculateProgress('customer');
  const salesRepProgress = calculateProgress('sales_rep');

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.level1' }}>
      {/* Header with signer toggle */}
      <Box sx={{
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.surface',
        boxShadow: 'sm',
      }}>
        <Stack direction="row" spacing={2} alignItems="center">
          {/* Exit button */}
          <IconButton
            variant="outlined"
            onClick={handleExit}
            sx={{ minWidth: '44px', minHeight: '44px' }}
          >
            <ExitToApp />
          </IconButton>

          {/* Contract info */}
          <Box sx={{ flex: 1 }}>
            <Typography level="h4" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
              {contract.contract_number}
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              {contract.customer_name}
            </Typography>
          </Box>

          {/* Signer toggle */}
          <ButtonGroup
            size="lg"
            sx={{
              '& .MuiButton-root': {
                minWidth: { xs: '120px', sm: '150px' },
                minHeight: '56px',
                fontSize: { xs: '1rem', sm: '1.1rem' },
              },
            }}
          >
            {(Object.keys(SIGNER_CONFIG) as SignerMode[]).map((mode) => {
              const config = SIGNER_CONFIG[mode];
              const Icon = config.icon;
              const progress = mode === 'customer' ? customerProgress : salesRepProgress;

              return (
                <Button
                  key={mode}
                  variant={signerMode === mode ? 'solid' : 'outlined'}
                  color={signerMode === mode ? config.color as any : 'neutral'}
                  onClick={() => setSignerMode(mode)}
                  startDecorator={<Icon />}
                  endDecorator={
                    progress.percentage === 100 && (
                      <CheckCircle size={19} color="var(--joy-palette-success-500)" />
                    )
                  }
                >
                  {config.label}
                </Button>
              );
            })}
          </ButtonGroup>

          {/* Complete button */}
          {isSigningComplete() && (
            <Button
              size="lg"
              color="success"
              variant="solid"
              onClick={handleCompleteSigning}
              disabled={saving}
              startDecorator={<CheckCircle />}
              sx={{
                minWidth: { xs: '140px', sm: '180px' },
                minHeight: '56px',
                fontSize: { xs: '1rem', sm: '1.1rem' },
              }}
            >
              Complete Signing
            </Button>
          )}
        </Stack>

        {/* Progress bars */}
        <Stack spacing={1} sx={{ mt: 2 }}>
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography level="body-sm" startDecorator={<Person />}>
                Customer Progress
              </Typography>
              <Typography level="body-sm">
                {customerProgress.completed}/{customerProgress.total} fields
              </Typography>
            </Stack>
            <LinearProgress
              determinate
              value={customerProgress.percentage}
              color="primary"
              sx={{ height: 8 }}
            />
          </Box>

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography level="body-sm" startDecorator={<Business />}>
                Sales Rep Progress
              </Typography>
              <Typography level="body-sm">
                {salesRepProgress.completed}/{salesRepProgress.total} fields
              </Typography>
            </Stack>
            <LinearProgress
              determinate
              value={salesRepProgress.percentage}
              color="success"
              sx={{ height: 8 }}
            />
          </Box>
        </Stack>

        {/* Current signer indicator */}
        <Alert
          color={SIGNER_CONFIG[signerMode].color as any}
          sx={{ mt: 2 }}
          startDecorator={React.createElement(SIGNER_CONFIG[signerMode].icon)}
        >
          Currently signing as: <strong>{SIGNER_CONFIG[signerMode].label}</strong>
        </Alert>

        {completed && (
          <Alert color="success" sx={{ mt: 2 }}>
            Contract signed successfully. Returning to the contract...
          </Alert>
        )}

        {error && (
          <Alert color="danger" sx={{ mt: 2 }} startDecorator={<Warning />}>
            {error}
          </Alert>
        )}
      </Box>

      {/* PDF viewer with fields — fills width, scrolls vertically */}
      <Box ref={pdfContainerRef} sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {numPages && numPages > 0 ? (
          Array.from({ length: numPages }, (_, index) => {
            const pageFields = convertedFields.filter(f => f.page === index);

            return (
              <Box key={`page_${index + 1}`} sx={{ mb: 2, position: 'relative' }}>
                <canvas
                  ref={(el) => {
                    if (el) canvasRefs.current.set(index + 1, el);
                  }}
                  style={{ display: 'block' }}
                />
                <SigningFieldRenderer
                  fields={pageFields}
                  fieldValues={Object.fromEntries(
                    Object.entries(fieldValues).map(([k, v]) => [String(k), v])
                  )}
                  onFieldValueChange={(fieldId, value) => {
                    handleFieldChange(fieldId, value);
                  }}
                  onSignatureFieldClick={handleSignatureFieldClick}
                  scale={scale}
                  touchOptimized={true}
                />
              </Box>
            );
          })
        ) : !loading && !pdfDoc ? (
          <Alert color="warning">No PDF available for this contract</Alert>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : null}
      </Box>

      {/* Exit confirmation dialog */}
      <Modal open={showExitDialog} onClose={() => setShowExitDialog(false)}>
        <ModalDialog data-joy-color-scheme="light">
          <DialogTitle>
            <Warning /> Exit Signing?
          </DialogTitle>
          <DialogContent>
            Signing is not complete. Are you sure you want to exit?{' '}
            {error ? 'Your last entry may not have been saved.' : 'All progress has been saved.'}
          </DialogContent>
          <DialogActions>
            <Button variant="plain" onClick={() => setShowExitDialog(false)}>
              Continue Signing
            </Button>
            <Button
              variant="solid"
              color="danger"
              onClick={() => navigate(`/crm/contracts/${contractId}`)}
            >
              Exit Anyway
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Saving indicator */}
      {saving && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            bgcolor: 'background.surface',
            p: 2,
            borderRadius: 'md',
            boxShadow: 'lg',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size="sm" />
            <Typography>Saving...</Typography>
          </Stack>
        </Box>
      )}

      {/* Signature Modal */}
      <SignatureModal
        open={signatureModalOpen}
        onClose={() => {
          setSignatureModalOpen(false);
          setActiveSignatureField(null);
        }}
        onSave={handleSignatureSave}
        fieldLabel={
          contract?.contract_fields.find(f => String(f.id) === activeSignatureField)?.label ||
          'Signature'
        }
        signerName={SIGNER_CONFIG[signerMode].label}
      />
    </Box>
  );
};
export const InPersonSigningInterface: React.FC = (props) => (
  <LightSigningSurface><InPersonSigningInterfaceContent {...props} /></LightSigningSurface>
);
